// ./src/hooks/useRoomTypes.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  RoomType,
  RoomTypesService,
  RoomTypeFilter,
  RoomTypeInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseRoomTypesOptions {
  /** Account ID to filter room types by */
  accountId?: string | undefined;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial search keyword */
  initialSearchKeyword?: string;

  /** Initial filters */
  initialFilters?: Partial<RoomTypeFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseRoomTypesReturn {
  // Data state
  roomType: RoomType | null;
  roomTypes: RoomType[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  searchKeyword: string;
  filters: Partial<RoomTypeFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingRoomTypeId: string | null;
  selectedRoomType: RoomType | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<RoomTypeFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<RoomType[]>;
  create: (params: RoomTypeInput) => Promise<RoomType | null>;
  update: (id: string, params: RoomTypeInput) => Promise<RoomType | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setSearchKeyword: (keyword: string) => void;
  setFilters: (filters: Partial<RoomTypeFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedRoomType: (roomType: RoomType | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  roomTypesByAccount: Record<string, RoomType[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "orderNo", direction: "asc" }];

export const useRoomTypes = (
  options: UseRoomTypesOptions = {}
): UseRoomTypesReturn => {
  const {
    accountId,
    pageSize = DEFAULT_PAGE_SIZE,
    initialSearchKeyword = "",
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  const accountIdRef = useRef(accountId);
  const searchKeywordRef = useRef(initialSearchKeyword);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    accountIdRef.current = accountId;
  }, [accountId]);

  // Data state
  const [roomType, setRoomType] = useState<RoomType | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [searchKeyword, setSearchKeyword] = useState(initialSearchKeyword);
  const [filters, setFilters] = useState<Partial<RoomTypeFilter>>(() => {
    const filter = { ...initialFilters };
    // Always include accountId if provided
    if (accountId) {
      filter.accountId = accountId;
    }
    return filter;
  });
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Update searchKeywordRef when searchKeyword state changes
  useEffect(() => {
    searchKeywordRef.current = searchKeyword;
  }, [searchKeyword]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingRoomTypeId, setRemovingRoomTypeId] = useState<string | null>(
    null
  );
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | null>(
    null
  );

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => roomTypes.length === 0 && !loading,
    [roomTypes.length, loading]
  );

  const roomTypesByAccount = useMemo(() => {
    return roomTypes.reduce((acc, roomType) => {
      const accountId = roomType.accountId || "unknown";
      if (!acc[accountId]) {
        acc[accountId] = [];
      }
      acc[accountId].push(roomType);
      return acc;
    }, {} as Record<string, RoomType[]>);
  }, [roomTypes]);

  // Helper function to update room type in list if it exists, or add it if not
  const updateOrAddRoomTypeInList = useCallback((updatedRoomType: RoomType) => {
    setRoomTypes((prev) => {
      const existingIndex = prev.findIndex(
        (rt) => rt.id === updatedRoomType.id
      );
      if (existingIndex >= 0) {
        const newRoomTypes = [...prev];
        newRoomTypes[existingIndex] = updatedRoomType;
        return newRoomTypes;
      } else {
        return [updatedRoomType, ...prev];
      }
    });
  }, []);

  // Helper function to remove room type from list if it exists
  const removeRoomTypeFromList = useCallback((roomTypeId: string) => {
    setRoomTypes((prev) => prev.filter((rt) => rt.id !== roomTypeId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setRoomType(null);
    setRoomTypes([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingRoomTypeId(null);
    setSelectedRoomType(null);
    setSearchKeyword(initialSearchKeyword);
  }, [initialSearchKeyword]);

  // Get single room type (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await RoomTypesService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const roomTypeData = result.data[0] as RoomType;
          setRoomType(roomTypeData);
          updateOrAddRoomTypeInList(roomTypeData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load room type";
          setLastError(errorMessage);
          setRoomType(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load room type";
        setLastError(errorMessage);
        setRoomType(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddRoomTypeInList]
  );

  // List room types (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<RoomTypeFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setRoomTypes([]);
          setCurrentPage(1);
        }
      }

      // Apply accountId filter if provided in options
      const combinedFilter = { ...filter };
      if (accountIdRef.current && !combinedFilter.accountId) {
        combinedFilter.accountId = accountIdRef.current;
      }

      // Apply search keyword to filter
      if (searchKeywordRef.current.trim()) {
        combinedFilter.name = searchKeywordRef.current.trim();
      }

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await RoomTypesService.list(
          combinedFilter as RoomTypeFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newRoomTypes = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setRoomTypes((prev) => {
              const existingIds = new Set(prev.map((rt) => rt.id));
              const uniqueNewRoomTypes = newRoomTypes.filter(
                (rt) => !existingIds.has(rt.id)
              );
              return [...prev, ...uniqueNewRoomTypes];
            });
          } else {
            setRoomTypes(newRoomTypes);
          }

          setHasMore(newRoomTypes.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load room types";
          setLastError(errorMessage);
          if (!isAppending) setRoomTypes([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load room types";
        setLastError(errorMessage);
        if (!isAppending) setRoomTypes([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many room types by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<RoomType[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await RoomTypesService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedRoomTypes = Array.isArray(result.data) ? result.data : [];
          loadedRoomTypes.forEach((roomType) => {
            updateOrAddRoomTypeInList(roomType);
          });
          setLastError(null);
          return loadedRoomTypes;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load room types";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load room types";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddRoomTypeInList]
  );

  // Create room type (matching API method name)
  const create = useCallback(
    async (params: RoomTypeInput): Promise<RoomType | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        // Ensure accountId is set if provided in options
        const roomTypeToCreate = {
          ...params,
          accountId: params.accountId || accountIdRef.current || undefined,
        };

        const result = await RoomTypesService.create(roomTypeToCreate);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newRoomType = result.data[0] as RoomType;
          setRoomType(newRoomType);
          setRoomTypes((prev) => [newRoomType, ...prev]);
          setLastError(null);
          return newRoomType;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create room type";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create room type";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update room type (matching API method name)
  const update = useCallback(
    async (id: string, params: RoomTypeInput): Promise<RoomType | null> => {
      if (!id) {
        setLastError("Room type ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await RoomTypesService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedRoomType = result.data[0] as RoomType;
          setRoomType(updatedRoomType);
          updateOrAddRoomTypeInList(updatedRoomType);
          setLastError(null);
          return updatedRoomType;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update room type";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update room type";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddRoomTypeInList]
  );

  // Remove room type (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Room type ID is required for removal");
        return false;
      }

      setRemovingRoomTypeId(id);
      setLastError(null);

      try {
        const result = await RoomTypesService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setRoomType(null);
          removeRoomTypeFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove room type";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove room type";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingRoomTypeId(null);
      }
    },
    [removeRoomTypeFromList]
  );

  // Load more room types
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh room types
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      const autoLoadFilters = { ...initialFilters };
      if (accountId) {
        autoLoadFilters.accountId = accountId;
      }
      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, accountId, pageSize, initialSort, list]);

  return {
    roomType,
    roomTypes,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    searchKeyword,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingRoomTypeId,
    selectedRoomType,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setSearchKeyword,
    setFilters,
    setSortBy,
    setSelectedRoomType,
    reset,
    roomTypesByAccount,
    lastError,
    clearError,
  };
};
